import {
  AccessTime,
  CheckCircle,
  Close,
  Code,
  GitHub,
  Launch,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Dialog,
  DialogContent,
  Fade,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import me from '../../constants/me';
import type { Project } from '../../constants/types';
import ProjectImageCarousel from '../misc/ProjectImageCarousel';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  background: 'transparent',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  background: 'rgba(34, 40, 49, 0.7)',
  backdropFilter: 'blur(15px)',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(5),
  border: '1px solid rgba(0, 173, 181, 0.1)',
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Gilroy, sans-serif',
  fontWeight: 800,
  fontSize: 'clamp(2rem, 4vw, 3rem)',
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  background: `linear-gradient(45deg, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 3,
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    borderRadius: 2,
    transition: 'width 0.8s ease-in-out',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 80,
    height: 3,
    background: 'rgba(0, 173, 181, 0.1)',
    borderRadius: 2,
  },
  '&.animate::after': {
    width: 80,
  },
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: 'clamp(1rem, 2vw, 1.2rem)',
  maxWidth: 600,
  margin: `${theme.spacing(6)}px auto ${theme.spacing(8)}px auto`,
  fontStyle: 'italic',
  position: 'relative',
  lineHeight: 1.6,
  paddingTop: theme.spacing(2),
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    whiteSpace: 'normal',
  },
}));

const ProjectCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(255, 255, 255, 0.1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 173, 181, 0.2)',
    border: '1px solid rgba(0, 173, 181, 0.3)',
  },
}));

const ProjectTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Gilroy, sans-serif',
  fontWeight: 700,
  fontSize: '1.25rem',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  },
}));

const ProjectDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  lineHeight: 1.6,
  marginBottom: theme.spacing(2),
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.85rem',
    WebkitLineClamp: 2,
  },
}));

const TechChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.7rem',
  height: 24,
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.main,
  '&:hover': {
    background: 'rgba(0, 173, 181, 0.1)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.65rem',
    height: 20,
  },
}));

const ProjectActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: 'auto',
  paddingTop: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  fontSize: '0.75rem',
  padding: theme.spacing(0.5, 1),
  minWidth: 'auto',
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
    padding: theme.spacing(0.75, 1),
    minWidth: 'unset',
    width: '100%',
    justifyContent: 'center',
  },
}));

const TechStack = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
}));

const DialogActionButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    flex: 1,
    minWidth: 'auto',
  },
}));

const DialogActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    alignItems: 'stretch',
  },
}));

const ProjectsSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animateTitle, setAnimateTitle] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setTimeout(() => setAnimateTitle(true), 300);
        }
      },
      { threshold: 0.2 }
    );

    const element = document.getElementById('projects');
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, []);

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedProject(null);
  };

  const handleExternalLink = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <SectionContainer id="projects">
      <Container maxWidth="lg">
        <ContentWrapper>
          <Fade in={isVisible} timeout={800}>
            <Box>
              <SectionTitle variant="h2" className={animateTitle ? 'animate' : ''}>
                Featured Projects
              </SectionTitle>
              <SectionSubtitle variant="subtitle1" sx={{textAlign: 'center',margin:'0 auto'}}>
                A showcase of my recent work and personal projects
              </SectionSubtitle>
            </Box>
          </Fade>
          <Box mb={2} mt={2}/>
          <Grid container spacing={4}>
            {me.projects.map((project: Project, index: number) => (
              <Grid item xs={12} md={6} lg={6} key={project.id}>
                <Slide direction="up" in={isVisible} timeout={1000 + index * 200}>
                  <Box sx={{ position: 'relative', height: '100%' }}>
                    <ProjectCard>
                      <CardMedia
                        component="div"
                        sx={{
                          height: { xs: 180, sm: 200 },
                          background: project.images.length > 0 
                            ? `url(${project.images[0]}) center/cover`
                            : 'linear-gradient(45deg, #00ADB5, #393E46)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0, 0, 0, 0.3)',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                          },
                          '&:hover::before': {
                            opacity: 1,
                          },
                          '&:hover': {
                            transform: 'scale(1.02)',
                          },
                        }}
                        onClick={() => handleProjectClick(project)}
                      >
                        {project.images.length === 0 && (
                          <Code sx={{ fontSize: '3rem', color: 'white', opacity: 0.7 }} />
                        )}
                      </CardMedia>
                      <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                        <ProjectTitle variant="h6">{project.title}</ProjectTitle>
                        <ProjectDescription variant="body2">
                          {project.description}
                        </ProjectDescription>
                        
                        <TechStack>
                          {project.technologies.slice(0, 3).map((tech: string, techIndex: number) => (
                            <TechChip key={techIndex} label={tech} size="small" />
                          ))}
                          {project.technologies.length > 3 && (
                            <TechChip label={`+${project.technologies.length - 3}`} size="small" />
                          )}
                        </TechStack>
                        
                        <ProjectActions>
                          <ActionButton
                            size="small"
                            variant="outlined"
                            startIcon={<Launch />}
                            onClick={() => handleExternalLink(project.liveUrl)}
                          >
                            Live Demo
                          </ActionButton>
                          {!project.private && (
                            <ActionButton
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={<GitHub />}
                              onClick={() => handleExternalLink(project.githubUrl)}
                            >
                              Code
                            </ActionButton>
                          )}
                          <ActionButton
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<Code />}
                            onClick={() => handleProjectClick(project)}
                          >
                            Details
                          </ActionButton>
                        </ProjectActions>
                      </CardContent>
                    </ProjectCard>
                  </Box>
                </Slide>
              </Grid>
            ))}
          </Grid>
        </ContentWrapper>
      </Container>

      {/* Project Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            background: 'linear-gradient(135deg, rgba(34, 40, 49, 0.95) 0%, rgba(57, 62, 70, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(0, 173, 181, 0.2)',
            borderRadius: '16px',
            margin: { xs: 1, sm: 2 },
            maxHeight: { xs: '95vh', sm: '90vh' },
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedProject && (
            <Box>
              <Box sx={{ position: 'relative' }}>
                <IconButton
                  onClick={handleCloseDialog}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    zIndex: 2,
                    background: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.7)',
                    },
                  }}
                >
                  <Close />
                </IconButton>
                
                {selectedProject.images && selectedProject.images.length > 0 && (
                  <ProjectImageCarousel 
                    images={selectedProject.images} 
                    alt={selectedProject.title}
                  />
                )}
              </Box>
              
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h4" sx={{ 
                  mb: 2, 
                  color: 'primary.main',
                  fontSize: { xs: '1.5rem', sm: '2rem' }
                }}>
                  {selectedProject.title}
                </Typography>
                
                <Typography variant="body1" sx={{ 
                  mb: 3, 
                  color: 'text.secondary',
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                }}>
                  {selectedProject.description}
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ 
                    mb: 1, 
                    color: 'text.primary',
                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                  }}>
                    Key Features
                  </Typography>
                  <List dense>
                    {selectedProject.features.map((feature: string, index: number) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          sx={{ 
                            '& .MuiListItemText-primary': { 
                              color: 'text.secondary',
                              fontSize: { xs: '0.85rem', sm: '0.9rem' }
                            } 
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ 
                    mb: 2, 
                    color: 'text.primary',
                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                  }}>
                    Technologies Used
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {selectedProject.technologies.map((tech: string, index: number) => (
                      <Chip
                        key={index}
                        label={tech}
                        variant="outlined"
                        sx={{
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          '&:hover': {
                            background: 'rgba(0, 173, 181, 0.1)',
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
                
                <DialogActions>
                  <DialogActionButton
                    variant="contained"
                    startIcon={<Launch />}
                    onClick={() => handleExternalLink(selectedProject.liveUrl)}
                  >
                    Live Demo
                  </DialogActionButton>
                  {!selectedProject.private && (
                    <DialogActionButton
                      variant="outlined"
                      startIcon={<GitHub />}
                      onClick={() => handleExternalLink(selectedProject.githubUrl)}
                      sx={{
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                          background: 'rgba(0, 173, 181, 0.1)',
                        },
                      }}
                    >
                      View Code
                    </DialogActionButton>
                  )}
                  {!isMobile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                      <AccessTime sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {selectedProject.duration}
                      </Typography>
                    </Box>
                  )}
                </DialogActions>
                
                {isMobile && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: 1, 
                    mt: 2,
                    pt: 2,
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <AccessTime sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {selectedProject.duration}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </SectionContainer>
  );
};

export default ProjectsSection; 